import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/context/AuthContext'
import { cartApi, type CartItemResponse } from '@/api/cart'
import { catalogApi, type ProductBadge } from '@/api/endpoints'
import { buildWarehouseScopedStorageKey } from '@/features/warehouse/config'
import { useWarehouse } from '@/context/WarehouseContext'
import { selectPreferredCardVariant } from '@/utils/productPricing'

export type CartItem = {
  /** ID строки корзины (с сервера) или составной ключ для гостя: productId_variantId */
  id: string
  /** ID товара (для ссылки на страницу товара и для заказа) */
  productId: number
  slug?: string
  title: string
  price: number
  originalPrice?: number | null
  discountPercent?: number | null
  badges?: ProductBadge[]
  promoBadgeText?: string | null
  image?: string
  inStock: boolean
  quantity: number
  selected: boolean
  variantId?: number
  variantSku?: string
  /** Макс. доступное количество (из варианта). Для ограничения в UI и при обновлении. */
  available_quantity?: number
}

type AddCartItemPayload = {
  /** product_id (number) или id товара для гостя */
  id: string
  productId?: number
  slug?: string
  title: string
  price: number
  originalPrice?: number | null
  discountPercent?: number | null
  badges?: ProductBadge[]
  promoBadgeText?: string | null
  image?: string
  inStock?: boolean
  quantity?: number
  variantId?: number
  variantSku?: string
  available_quantity?: number
}

type CartContextValue = {
  items: CartItem[]
  isReady: boolean
  itemCount: number
  selectedCount: number
  subtotal: number
  selectedSubtotal: number
  addItem: (item: AddCartItemPayload) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number, maxQuantity?: number) => void
  toggleSelect: (id: string) => void
  toggleSelectAll: (selected: boolean) => void
  clearSelected: () => void
  reloadFromServer: () => Promise<void>
}

const CART_STORAGE_KEY = 'nurm_cart_v1'

const CartContext = createContext<CartContextValue | null>(null)

const parsePrice = (value: number | string) => {
  const normalized = String(value).replace(/\s/g, '').replace(',', '.')
  return parseFloat(normalized) || 0
}

const normalizePromoBadgeText = (value: string | null | undefined): string | null => {
  const normalized = value?.trim()
  if (!normalized || normalized.toLowerCase() === 'null') return null
  return normalized
}

/** Форматирует цену для отображения (ru-RU, без округления) */
export const formatPrice = (value: number | string) => {
  const n = typeof value === 'number' ? value : parsePrice(value)
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(n) + ' ₽'
}

function serverItemToCartItem(r: CartItemResponse): CartItem {
  const available = r.available_quantity ?? 0
  const finalPrice =
    r.final_price != null ? parsePrice(r.final_price) : parsePrice(r.price)
  const basePrice =
    r.base_price != null ? parsePrice(r.base_price) : finalPrice
  const discountPercent =
    r.discount_percent != null ? parsePrice(r.discount_percent) : null
  return {
    id: String(r.id),
    productId: r.product_id,
    slug: r.product_slug ?? undefined,
    title: r.product_name,
    price: finalPrice,
    originalPrice: basePrice > finalPrice ? basePrice : null,
    discountPercent: discountPercent && discountPercent > 0 ? discountPercent : null,
    badges: r.badges ?? [],
    promoBadgeText: normalizePromoBadgeText(r.promo_badge_text),
    image: r.image ?? undefined,
    inStock: available > 0,
    quantity: Math.min(r.quantity, Math.max(0, available)),
    selected: finalPrice > 0 && available > 0 && r.quantity > 0,
    variantId: r.variant_id ?? undefined,
    variantSku: r.variant_sku ?? undefined,
    available_quantity: available,
  }
}

function normalizeGuestCartItem(item: CartItem): CartItem {
  if (item.variantId != null) return item

  // Старые гостевые строки сохраняли вариант только в составном ключе
  // productId_variantId. Восстанавливаем его при загрузке корзины.
  const parts = String(item.id).split('_')
  const variantId = parts.length === 2 ? Number(parts[1]) : 0
  return variantId > 0 && Number.isInteger(variantId)
    ? { ...item, variantId }
    : item
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { selectedWarehouseId } = useWarehouse()
  const { token, isAuthenticated } = useAuth()
  const [items, setItems] = useState<CartItem[]>([])
  const quantityRequestVersions = useRef(new Map<string, number>())
  const [isReady, setIsReady] = useState(false)
  const loadedFromServer = useRef(false)
  const [guestStorageReadyKey, setGuestStorageReadyKey] = useState<string | null>(null)

  const cartStorageKey = buildWarehouseScopedStorageKey(CART_STORAGE_KEY, selectedWarehouseId)

  // Гость: загрузка из localStorage
  useEffect(() => {
    if (isAuthenticated) return
    setIsReady(false)
    let cancelled = false
    try {
      const raw = localStorage.getItem(cartStorageKey)
      if (!raw) {
        setItems([])
        setIsReady(true)
        setGuestStorageReadyKey(cartStorageKey)
        return
      }
      const parsed = JSON.parse(raw) as CartItem[]
      const normalizedItems = Array.isArray(parsed)
        ? parsed.map((item) => ({
            ...normalizeGuestCartItem(item),
            promoBadgeText: normalizePromoBadgeText(item.promoBadgeText),
          }))
        : []
      setItems(normalizedItems.map((item) => (
        item.price > 0 ? item : { ...item, selected: false }
      )))
      setIsReady(true)

      // Для старых гостевых строк вариант мог вообще не сохраниться.
      // Восстанавливаем предпочтительный доступный вариант по товару.
      const missingVariants = normalizedItems.filter((item) => item.variantId == null)
      if (missingVariants.length > 0) {
        Promise.all(
          missingVariants.map(async (item) => {
            try {
              const product = await catalogApi.getProduct(item.slug ?? String(item.productId))
              return { productId: item.productId, variantId: selectPreferredCardVariant(product.variants)?.id }
            } catch {
              return { productId: item.productId, variantId: undefined }
            }
          }),
        ).then((resolved) => {
          if (cancelled) return
          const variantsByProduct = new Map(
            resolved
              .filter((item): item is { productId: number; variantId: number } => item.variantId != null)
              .map((item) => [item.productId, item.variantId]),
          )
          if (variantsByProduct.size === 0) return
          setItems((current) =>
            current.map((item) => {
              const variantId = variantsByProduct.get(item.productId)
              return item.variantId == null && variantId != null ? { ...item, variantId } : item
            }),
          )
        })
      }
    } catch {
      setItems([])
      setIsReady(true)
    } finally {
      setGuestStorageReadyKey(cartStorageKey)
    }
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, cartStorageKey])

  // Гость: сохранение в localStorage
  useEffect(() => {
    if (isAuthenticated) return
    if (guestStorageReadyKey !== cartStorageKey) return
    localStorage.setItem(cartStorageKey, JSON.stringify(items))
  }, [isAuthenticated, items, cartStorageKey, guestStorageReadyKey])

  // Авторизован: загрузка с сервера при появлении токена
  const reloadFromServer = useCallback(async () => {
    if (!token) return
    try {
      const data = await cartApi.getCart()
      setItems(data.map(serverItemToCartItem))
      setIsReady(true)
      loadedFromServer.current = true
    } catch {
      setItems([])
      setIsReady(true)
    }
  }, [token])

  useEffect(() => {
    if (!isAuthenticated || !token) {
      loadedFromServer.current = false
      if (isAuthenticated) setIsReady(false)
      return
    }
    setIsReady(false)
    reloadFromServer()
  }, [isAuthenticated, token, reloadFromServer, selectedWarehouseId])

  const addItem = useCallback(
    (item: AddCartItemPayload) => {
      const productId = item.productId ?? Number(item.id)
      if (Number.isNaN(productId)) return

      if (token) {
        const qty = Math.max(1, item.quantity ?? 1)
        cartApi
          .addItem(productId, qty, item.variantId)
          .then((r) => {
            setItems((prev) => {
              const existing = prev.find((it) => it.id === String(r.id))
              if (existing) {
                return prev.map((it) =>
                  it.id === String(r.id)
                    ? { ...serverItemToCartItem(r), selected: it.selected && serverItemToCartItem(r).price > 0 }
                    : it
                )
              }
              return [...prev, serverItemToCartItem(r)]
            })
          })
          .catch(() => {})
        return
      }

      const variantId = item.variantId
      const guestId = `${productId}_${variantId ?? 0}`
      setItems((prev) => {
        const existing = prev.find((it) => it.id === guestId)
        if (existing) {
          const addQty = item.quantity ?? 1
          const available = item.available_quantity ?? existing.available_quantity ?? 999
          const newQty = Math.min(existing.quantity + addQty, Math.max(0, available))
          return prev.map((it) =>
            it.id === guestId
              ? {
                  ...it,
                  quantity: newQty,
                  inStock: available > 0,
                  available_quantity: available,
                }
              : it
          )
        }
        const available = item.available_quantity ?? 999
        const qty = Math.min(Math.max(1, item.quantity ?? 1), Math.max(0, available))
        return [
          ...prev,
          {
            id: guestId,
            productId,
            slug: item.slug,
            title: item.title,
            price: item.price,
            originalPrice: item.originalPrice ?? null,
            discountPercent: item.discountPercent ?? null,
            badges: item.badges ?? [],
            promoBadgeText: normalizePromoBadgeText(item.promoBadgeText),
            image: item.image,
            inStock: available > 0,
            quantity: qty,
            selected: item.price > 0 && available > 0 && qty > 0,
            variantSku: item.variantSku,
            available_quantity: available,
            ...(variantId != null ? { variantId } : {}),
          },
        ]
      })
    },
    [token]
  )

  const removeItem = useCallback(
    (id: string) => {
      if (token) {
        const cartLineId = Number(id)
        if (!Number.isNaN(cartLineId)) {
          cartApi.removeItem(cartLineId).then(() => {
            setItems((prev) => prev.filter((it) => it.id !== id))
          }).catch(() => {})
        }
        return
      }
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [token]
  )

  const updateQuantity = useCallback(
    (id: string, quantity: number, maxQuantity?: number) => {
      if (token) {
        const item = items.find((it) => it.id === id)
        if (!item) return
        const maxQty = maxQuantity ?? item.available_quantity ?? 999
        const qty = Math.min(Math.max(1, quantity), Math.max(0, maxQty))

        const version = (quantityRequestVersions.current.get(id) ?? 0) + 1
        quantityRequestVersions.current.set(id, version)
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, quantity: qty } : it)))

        cartApi.addItem(item.productId, qty, item.variantId).then((r) => {
          if (quantityRequestVersions.current.get(id) !== version) return
          setItems((prev) =>
            prev.map((it) =>
              it.id === id
                ? { ...serverItemToCartItem(r), selected: it.selected && serverItemToCartItem(r).price > 0 }
                : it
            )
          )
        }).catch(() => {})
        return
      }
      const item = items.find((it) => it.id === id)
      const maxQty = maxQuantity ?? item?.available_quantity ?? 999
      const qty = Math.min(Math.max(1, quantity), Math.max(0, maxQty))
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, quantity: qty } : it))
      )
    },
    [token, items]
  )

  const toggleSelect = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((it) => (
        it.id === id && it.price > 0 ? { ...it, selected: !it.selected } : it
      ))
    )
  }, [])

  const toggleSelectAll = useCallback((selected: boolean) => {
    setItems((prev) => prev.map((it) => ({ ...it, selected: selected && it.price > 0 })))
  }, [])

  const clearSelected = useCallback(() => {
    setItems((prev) => prev.filter((it) => !it.selected))
  }, [])

  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  )
  const selectedCount = useMemo(
    () =>
      items
        .filter((item) => item.selected)
        .reduce((sum, item) => sum + item.quantity, 0),
    [items]
  )
  const subtotal = useMemo(
    () =>
      items.reduce((sum, item) => sum + parsePrice(item.price) * item.quantity, 0),
    [items]
  )
  const selectedSubtotal = useMemo(
    () =>
      items
        .filter((item) => item.selected)
        .reduce(
          (sum, item) => sum + parsePrice(item.price) * item.quantity,
          0
        ),
    [items]
  )

  const value = useMemo(
    () => ({
      items,
      isReady,
      itemCount,
      selectedCount,
      subtotal,
      selectedSubtotal,
      addItem,
      removeItem,
      updateQuantity,
      toggleSelect,
      toggleSelectAll,
      clearSelected,
      reloadFromServer,
    }),
    [
      items,
      isReady,
      itemCount,
      selectedCount,
      subtotal,
      selectedSubtotal,
      addItem,
      removeItem,
      updateQuantity,
      toggleSelect,
      toggleSelectAll,
      clearSelected,
      reloadFromServer,
    ]
  )

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  )
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider')
  }
  return ctx
}
